import { twMerge } from 'tailwind-merge';
import { useFile, useFileVersionFromFileId } from '@witmetrics/data-store';
import { isPdf } from '@witmetrics/utils/files';
import { useToggle } from '@/hooks/useToggle';
import { fetchFileVersion, openFileDownloadLink } from '@/utils/files';
import { showErrorToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import Divider from '@/components/Divider';
import OptionButton from '@/components/ViewerHeader/OptionButton';
import {
  CloseIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  MessagesIcon,
  PrinterIcon,
  ShareIcon,
} from '@/icons';
import ShareFileDialog from '@/dialogs/ShareFileDialog';
import FileInfoDialog from '@/dialogs/FileInfoDialog';
import DeleteFileDialog from '@/dialogs/DeleteFileDialog';
import DownloadToaster from '@/toasters/DownloadToaster';
import { classes } from './utils';

export type FileControlsProps = {
  showTimeline?: boolean;
  fileId: number;
  onToggleTimeline?: (open: boolean) => void;
  onClose?: () => void;
};

export default function FileControls({
  fileId,
  showTimeline,
  onToggleTimeline,
  onClose,
}: FileControlsProps) {
  const { onApiError } = useAppState();
  const file = useFile(fileId);
  const fileVersion = useFileVersionFromFileId(fileId);
  const { loadedFiles } = useLoadedFiles();
  const [isShareDialogOpen, toggleShareDialog] = useToggle();
  const [isEditDialogOpen, toggleEditDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();

  const handleDownload = async () => {
    try {
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      let version: typeof fileVersion | null = fileVersion;
      if (!version) {
        version = await fetchFileVersion(file!.id);
        if (!version) return showErrorToaster('File version not found');
      }
      await openFileDownloadLink(version!.id, file!.name);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading file');
    }
  };

  const handlePrint = () => {
    if (!(loadedFiles[fileId] instanceof Blob)) {
      return showErrorToaster(
        'Unable to print file. Please download and print manually.'
      );
    }
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';

    iframe.src = URL.createObjectURL(loadedFiles[fileId] as Blob);

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };

    document.body.appendChild(iframe);
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  if (!file) return null;

  return (
    <>
      {isShareDialogOpen && (
        <ShareFileDialog
          fileId={fileId}
          onClose={() => toggleShareDialog(false)}
        />
      )}
      {isEditDialogOpen && (
        <FileInfoDialog fileId={fileId} onClose={() => toggleEditDialog()} />
      )}
      {isDeleteDialogOpen && (
        <DeleteFileDialog
          fileId={fileId}
          onDelete={onClose}
          onClose={() => toggleDeleteDialog()}
        />
      )}
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message="Preparing file..."
        onClose={handleCloseDownloadToaster}
      />
      <div className={classes.wrapper}>
        {onToggleTimeline && (
          <>
            <OptionButton
              title={`${showTimeline ? 'Hide' : 'Show'} notes`}
              onClick={() => onToggleTimeline(!showTimeline)}>
              <MessagesIcon
                className={twMerge(
                  classes.icon,
                  showTimeline && classes.activeIcon
                )}
              />
            </OptionButton>
            <Divider
              flexItem
              className={classes.divider}
              orientation="vertical"
            />
          </>
        )}
        {isPdf(file.name) && (
          <OptionButton title="Print" onClick={handlePrint}>
            <PrinterIcon className={classes.icon} />
          </OptionButton>
        )}
        <OptionButton title="Download" onClick={handleDownload}>
          <DownloadIcon className={classes.icon} />
        </OptionButton>
        <OptionButton title="Share" onClick={toggleShareDialog}>
          <ShareIcon className={classes.icon} />
        </OptionButton>
        <OptionButton title="Edit" onClick={toggleEditDialog}>
          <EditIcon className={classes.icon} />
        </OptionButton>
        <OptionButton
          className={classes.deleteButton}
          title="Delete"
          onClick={toggleDeleteDialog}>
          <DeleteIcon className={twMerge(classes.icon, classes.deleteIcon)} />
        </OptionButton>

        {onClose && (
          <>
            <Divider
              flexItem
              className={classes.divider}
              orientation="vertical"
            />
            <OptionButton title="Close" onClick={onClose}>
              <CloseIcon className={classes.icon} />
            </OptionButton>
          </>
        )}
      </div>
    </>
  );
}
